import { getSupertoken } from './mailService';
import { QuestionnaireFormData } from '../ui/pages/questionnaire/context/QuestionnaireFormContext';
import { saveImage, ImageKey, StorageContext } from './indexedDBService';

const GITHUB_API_BASE = 'https://api.github.com';
const REPO_OWNER = 'Milszym';
const REPO_NAME = 'weddie-site';

/**
 * Fetches a file content from GitHub repository
 */
const fetchFileFromGitHub = async (
    token: string,
    branchName: string,
    filePath: string
): Promise<string> => {
    const response = await fetch(
        `${GITHUB_API_BASE}/repos/${REPO_OWNER}/${REPO_NAME}/contents/${filePath}?ref=${branchName}`,
        {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/vnd.github.v3+json'
            }
        }
    );
    
    if (!response.ok) {
        throw new Error(`Failed to fetch file ${filePath}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    const base64Content = data.content;
    const binaryString = atob(base64Content.replace(/\n/g, ''));
    const bytes = Uint8Array.from(binaryString, c => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
};

/**
 * Converts base64 to data URL for images
 */
const base64ToDataUrl = (base64Content: string, mimeType: string = 'image/png'): string => {
    return `data:${mimeType};base64,${base64Content}`;
};

/**
 * Fetches questionnaire data from a GitHub branch
 * @param branchName - The branch name to fetch from
 * @returns QuestionnaireFormData with images as base64 data URLs
 */
export const fetchQuestionnaireFromGitHub = async (
    branchName: string,
    storageContext: StorageContext = 'questionnaire'
): Promise<QuestionnaireFormData> => {
    try {
        console.log(`Fetching questionnaire data from branch: ${branchName}`);
        
        // Step 1: Get GitHub token
        const { token } = await getSupertoken();
        
        // Step 2: Fetch config.json
        console.log('Fetching config.json...');
        const configContent = await fetchFileFromGitHub(token, branchName, 'src/mainConfig/config.json');
        const configData = JSON.parse(configContent);
        
        // Step 3: Prepare image fetch list with IndexedDB keys
        const imagesToFetch: Array<{
            path: string;
            indexedDBKey: ImageKey;
            configPath: keyof QuestionnaireFormData;
            nestedPath?: string[];
        }> = [];
        if (configData.mainImages?.desktopImage) {
            imagesToFetch.push({
                path: 'public/images/desktopImage.png',
                indexedDBKey: ImageKey.DESKTOP_IMAGE,
                configPath: 'mainImages',
                nestedPath: ['desktopImage']
            });
        }
        
        if (configData.mainImages?.mobileImage) {
            imagesToFetch.push({
                path: 'public/images/mobileImage.png',
                indexedDBKey: ImageKey.MOBILE_IMAGE,
                configPath: 'mainImages',
                nestedPath: ['mobileImage']
            });
        }
        
        if (configData.witnesses?.witness1?.image) {
            imagesToFetch.push({
                path: 'public/images/witness1.png',
                indexedDBKey: ImageKey.WITNESS1_IMAGE,
                configPath: 'witnesses',
                nestedPath: ['witness1', 'image']
            });
        }
        
        if (configData.witnesses?.witness2?.image) {
            imagesToFetch.push({
                path: 'public/images/witness2.png',
                indexedDBKey: ImageKey.WITNESS2_IMAGE,
                configPath: 'witnesses',
                nestedPath: ['witness2', 'image']
            });
        }
        
        // Fetch all images and save to IndexedDB
        for (const imageInfo of imagesToFetch) {
            try {
                console.log(`Fetching ${imageInfo.path}...`);
                
                // First, get file metadata to get the download URL or git blob SHA
                const metadataResponse = await fetch(
                    `${GITHUB_API_BASE}/repos/${REPO_OWNER}/${REPO_NAME}/contents/${imageInfo.path}?ref=${branchName}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Accept': 'application/vnd.github.v3+json'
                        }
                    }
                );
                
                if (metadataResponse.ok) {
                    const metadata = await metadataResponse.json();
                    
                    // Use the git blob API to get the raw base64 content
                    // This is more reliable for binary files like images
                    if (metadata.sha) {
                        console.log(`Fetching blob for ${imageInfo.path} with SHA ${metadata.sha.substring(0, 7)}...`);
                        const blobResponse = await fetch(
                            `${GITHUB_API_BASE}/repos/${REPO_OWNER}/${REPO_NAME}/git/blobs/${metadata.sha}`,
                            {
                                headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Accept': 'application/vnd.github.v3+json'
                                }
                            }
                        );
                        
                        if (blobResponse.ok) {
                            const blobData = await blobResponse.json();
                            
                            if (blobData.content && blobData.encoding === 'base64') {
                                const base64Content = blobData.content.replace(/\n/g, '');
                                const dataUrl = base64ToDataUrl(base64Content);
                                
                                // Save image to IndexedDB
                                await saveImage(imageInfo.indexedDBKey, dataUrl, storageContext);
                                console.log(`Saved ${imageInfo.path} to IndexedDB (${(base64Content.length / 1024).toFixed(2)} KB)`);
                                
                                // Set boolean flag in config data
                                if (imageInfo.nestedPath) {
                                    let current: any = configData[imageInfo.configPath];
                                    for (let i = 0; i < imageInfo.nestedPath.length - 1; i++) {
                                        current = current[imageInfo.nestedPath[i]];
                                    }
                                    current[imageInfo.nestedPath[imageInfo.nestedPath.length - 1]] = true;
                                }
                            } else {
                                console.warn(`Blob for ${imageInfo.path} is not base64 encoded or has no content`);
                            }
                        } else {
                            console.warn(`Failed to fetch blob for ${imageInfo.path}: ${blobResponse.statusText}`);
                        }
                    } else {
                        console.warn(`No SHA found for ${imageInfo.path}`);
                    }
                } else {
                    console.warn(`Failed to fetch metadata for ${imageInfo.path}: ${metadataResponse.statusText}`);
                    // Set flag to false if image doesn't exist
                    if (imageInfo.nestedPath) {
                        let current: any = configData[imageInfo.configPath];
                        for (let i = 0; i < imageInfo.nestedPath.length - 1; i++) {
                            current = current[imageInfo.nestedPath[i]];
                        }
                        current[imageInfo.nestedPath[imageInfo.nestedPath.length - 1]] = false;
                    }
                }
            } catch (error) {
                console.warn(`Error fetching image ${imageInfo.path}:`, error);
                // Set flag to false on error
                if (imageInfo.nestedPath) {
                    let current: any = configData[imageInfo.configPath];
                    for (let i = 0; i < imageInfo.nestedPath.length - 1; i++) {
                        current = current[imageInfo.nestedPath[i]];
                    }
                    current[imageInfo.nestedPath[imageInfo.nestedPath.length - 1]] = false;
                }
            }
        }
        
        console.log('Successfully fetched questionnaire data from GitHub!');
        return configData as QuestionnaireFormData;
        
    } catch (error) {
        console.error('Error fetching questionnaire from GitHub:', error);
        throw error;
    }
};

/**
 * Decodes a base64 encoded branch name
 */
export const decodeBranchName = (base64BranchName: string): string => {
    try {
        return atob(base64BranchName);
    } catch (error) {
        console.error('Error decoding branch name:', error);
        throw new Error('Invalid branch name encoding');
    }
};
